import { useCallback, useEffect, useState } from 'react';

import { useAuthStore } from '@/stores/auth-store';

/**
 * Most-recently-used nav-item store. localStorage-only, scoped per user id
 * so multiple operators on the same browser don't see each other's history.
 *
 * Server-side persistence is a deliberate follow-up; this hook is the
 * single read/write surface we'll keep when that lands.
 */

interface MruEntry {
  /** NavItemConfig.id - stable identifier from `nav-config.tsx`. */
  id: string;
  /** UNIX ms of the most recent visit. */
  lastUsedAt: number;
  /** Visit count - secondary sort, ages slower than recency. */
  count: number;
}

const STORAGE_PREFIX = 'utazzo:nav-mru:';
const MAX_ENTRIES = 12;
const STORAGE_EVENT = 'utazzo:nav-mru-changed';

const storageKey = (userId: string | null | undefined): string =>
  `${STORAGE_PREFIX}${userId ?? 'anon'}`;

const readMru = (userId: string | null | undefined): MruEntry[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is MruEntry =>
        e !== null &&
        typeof e === 'object' &&
        typeof (e as MruEntry).id === 'string' &&
        typeof (e as MruEntry).lastUsedAt === 'number' &&
        typeof (e as MruEntry).count === 'number',
    );
  } catch {
    return [];
  }
};

const writeMru = (userId: string | null | undefined, entries: MruEntry[]): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey(userId), JSON.stringify(entries));
  // In-tab subscribers don't get the native `storage` event; emit our own.
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
};

/**
 * Returns the user's recent nav items (most recent first) plus a `bump`
 * callback to record a visit. The hook stays in sync across components via
 * a custom `nav-mru-changed` event so the sidebar, palette, and dashboard
 * strip all reflect the same list immediately.
 */
export const useNavMru = (): {
  entries: MruEntry[];
  bump: (id: string) => void;
} => {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [entries, setEntries] = useState<MruEntry[]>(() => readMru(userId));

  useEffect(() => {
    setEntries(readMru(userId));
    const onChange = (): void => setEntries(readMru(userId));
    window.addEventListener(STORAGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(STORAGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [userId]);

  const bump = useCallback(
    (id: string): void => {
      const now = Date.now();
      const current = readMru(userId);
      const existing = current.find((e) => e.id === id);
      const others = current.filter((e) => e.id !== id);
      const next: MruEntry[] = [
        { id, lastUsedAt: now, count: (existing?.count ?? 0) + 1 },
        ...others,
      ].slice(0, MAX_ENTRIES);
      writeMru(userId, next);
      setEntries(next);
    },
    [userId],
  );

  return { entries, bump };
};
